import React from 'react';

/**
 * A column header you can click to sort, with the affordance that says so.
 *
 * The reports that prompted this said the headers "look sortable and do nothing". Half of
 * that was wrong — they carried no cursor-pointer and no arrows, so they did not actually
 * signal anything — but the fix is the same either way: make them sortable AND make them look
 * it. An idle column shows a dim ↕ so you can tell the column is interactive before you
 * click it; the active one shows the direction.
 *
 * Renders a <button> inside the <th> rather than putting onClick on the <th> itself, which is
 * what pages/JobsHistory.js does. A th with a click handler is not focusable and not
 * reachable by keyboard, so that table can only be sorted with a mouse. Pair this with
 * `aria-sort` on the th (see ariaSortFor in utils/tableSort.js) so the state is announced
 * rather than only drawn.
 */
const SortableHeader = ({ sortKey, sortConfig, onSort, children, className = '', align = 'left' }) => {
  const active = sortConfig?.key === sortKey;
  const direction = active ? sortConfig.direction : null;

  const justify = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      // The visible label already names the column, so the accessible name only needs to add
      // what the control DOES — otherwise a screen reader hears the column name twice.
      title={`Sort by ${typeof children === 'string' ? children : sortKey}`}
      // Tailwind's preflight sets `text-transform: none` on buttons, which silently undid the
      // `uppercase` every header cell carries — sortable columns read "Vendor" beside "ACTIONS".
      style={{ textTransform: 'inherit' }}
      className={`inline-flex w-full items-center gap-1 ${justify} select-none transition-colors hover:text-primary-600 dark:hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded ${className}`}
    >
      <span>{children}</span>
      <span
        aria-hidden="true"
        className={`text-[10px] leading-none ${active ? 'opacity-100' : 'opacity-40'}`}
      >
        {active ? (direction === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
};

export default SortableHeader;
